import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useAuthContext } from '@/contexts/AuthContext';
import {
  recentlyViewedKey,
  getRecentlyViewed,
  clearRecentlyViewed,
  removeRecentlyViewed,
  type RecentlyViewedEntry,
} from '@/lib/recentlyViewed';

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d');
  } catch {
    return iso;
  }
}

function fmtRelative(epochMs: number): string {
  try {
    return formatDistanceToNow(new Date(epochMs), { addSuffix: true });
  } catch {
    return '';
  }
}

/**
 * Recently-viewed reservations panel (Flow 7b). Reads localStorage synchronously
 * (no loading state) and re-reads on property change (key depends on propertyId).
 * Row click navigates to /reservations?open=<id>, pruning the entry first so a
 * stale booking self-heals out of the list.
 */
export function RecentlyViewedPanel() {
  const navigate = useNavigate();
  const propertyId = useSelectedPropertyId();
  const { user } = useAuthContext();
  const key = recentlyViewedKey(user?.tenantId, propertyId);

  const [open, setOpen] = useState(true);
  // Local mirror; re-derived on key change and after mutations.
  const [entries, setEntries] = useState<RecentlyViewedEntry[]>(() => getRecentlyViewed(key));

  // Re-read whenever the scope key changes (property/tenant switch).
  const [lastKey, setLastKey] = useState(key);
  if (lastKey !== key) {
    setLastKey(key);
    setEntries(getRecentlyViewed(key));
  }

  const handleOpen = useCallback(
    (id: string) => {
      removeRecentlyViewed(key, id);
      setEntries(getRecentlyViewed(key));
      navigate(`/reservations?open=${id}`);
    },
    [key, navigate],
  );

  const handleClear = useCallback(() => {
    clearRecentlyViewed(key);
    setEntries([]);
  }, [key]);

  return (
    <div className="bg-white rounded-xl border border-gray-200 mb-6">
      <div className="flex items-center justify-between px-4 py-3">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-2 text-sm font-semibold text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          aria-expanded={open}
        >
          <Clock size={16} className="text-gray-400" aria-hidden="true" />
          Recently Viewed
          {open ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
        </button>
        {entries.length > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-gray-400 hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
          >
            Clear
          </button>
        )}
      </div>

      {open && (
        <div className="border-t border-gray-100">
          {entries.length === 0 ? (
            <p className="px-4 py-4 text-sm text-gray-400">
              No recently viewed reservations on this device.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {entries.map((e) => (
                <li key={e.id}>
                  <button
                    type="button"
                    onClick={() => handleOpen(e.id)}
                    className={cn(
                      'w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left',
                      'hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:z-10',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-gray-900 truncate">{e.guestName}</span>
                      <span className="block text-xs text-gray-500 truncate">
                        {e.unitName} · {fmtDate(e.checkIn)} – {fmtDate(e.checkOut)}
                      </span>
                    </span>
                    <span className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                      {fmtRelative(e.viewedAt)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
